import React, { useEffect } from "react";
import { TableComponents } from './TableComponents.tsx';
import { GameTeamComponent } from "./GameTeamComponent.tsx";
import { Schedule } from "../../../../models/Schedule.tsx";
import { useUser } from "../../../../context/UserContext.tsx";
import { Odd } from "../../../../models/Odd_response_model.tsx";
import { Link } from "react-router-dom";
import { getDrawOddsGroup } from "../../../../helpers/functions.tsx";
import { MobileColumnTab } from "./mobileMarketColumns.ts";

const getOrdinalSuffix = (n: number): string => {
    const suffixes = ['TH', 'ST', 'ND', 'RD'];
    const v = n % 100;
    return suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
};

const formatTournamentStage = (stage?: string): string => {
    if (!stage) return '';
    const roundMatch = stage.match(/round\s+(\d+)/i);
    if (roundMatch) {
        const round = parseInt(roundMatch[1], 10);
        return `${round}${getOrdinalSuffix(round)} RND `;
    }
    return stage.toUpperCase();
};

const formatTournamentName = (name?: string): string => {
    if (!name) return '';
    return name.replace(/\s+\d{4}$/, '').toUpperCase();
};

const formatGolfEventLabel = (game: Schedule): string => {
    const parts = [
        formatTournamentName(game.tournament?.name),
        formatTournamentStage(game.tournament_stage),
    ].filter(Boolean);

    const venue = game.venue_name?.trim();
    if (venue) {
        parts.push(`@ ${venue.toUpperCase()}`);
    }

    return parts.join(' ');
};

interface IgameComponents {
    game: Schedule;
    showHeaderByLeague: boolean;
    showHeaderByDate: boolean;
    onShowBetsModal: Function;
    pitchersChecked: { away: boolean; home: boolean };
    onPitcherChange: (gameId: string, team: "away" | "home") => void;
    setLoadingBet: React.Dispatch<React.SetStateAction<boolean>>;
    loadingBet: boolean;
    rowIndex?: number;
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
    mobileColumnTab: MobileColumnTab;
    showMoneylineSlot: boolean;
    onMobileColumnTabChange: React.Dispatch<React.SetStateAction<MobileColumnTab>>;
    onShowMoneylineSlotChange: React.Dispatch<React.SetStateAction<boolean>>;
}

export function GameComponents(props: IgameComponents) {

    const { loadingBet, game, showHeaderByLeague, showHeaderByDate, onShowBetsModal, pitchersChecked, onPitcherChange, setLoadingBet, setErrorMessage,
        mobileColumnTab, showMoneylineSlot, onMobileColumnTabChange, onShowMoneylineSlotChange } = props;
    const { user } = useUser();
    if (!user?.userName) {
        throw new Error("User not authenticated");
    }

    const formattedDate = new Date(game.start_date).toLocaleDateString('en-US', {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    }).toUpperCase();

    const gameTimeDate = new Date(game.start_date ?? '')

    const time = gameTimeDate.toLocaleTimeString('en-US', {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: "short"
    });

    const isGolf = game.sport?.id === 'golf';

    const handlePitcherChangeInternal = (team: "away" | "home") => { onPitcherChange(game.game_id, team); };

    const [organizedOdds, setOrganizedOdds] = React.useState<Odd[][]>([]);

    const [theRestOfOdds, setTheRestOfOdds] = React.useState<Odd[][]>([]);

    useEffect(() => {
        const groupOddsByMarket = (Object.groupBy(game.odds ?? [], odd => odd.market_id) as { [key: string]: Odd[] }) ?? {};
        const marketsId = Object.keys(groupOddsByMarket);

        const tmpHomeOdds = marketsId.map(marketId => {
            return groupOddsByMarket[marketId].filter(
                o => o.selection === game.home_team_display || (o.selection_line === 'under' && o.selection === "")
            );
        }).flat();

        const tmpAwayOdds = marketsId.map(marketId => {
            return groupOddsByMarket[marketId].filter(
                o => o.selection === game.away_team_display || (o.selection_line === 'over' && o.selection === "")
            );
        }).flat();

        setTheRestOfOdds(getDrawOddsGroup(game.odds));
        setOrganizedOdds([tmpHomeOdds, tmpAwayOdds]);
    }, [game.odds, game.home_team_display, game.away_team_display]);

    const formatPitcherName = (name?: string): string => {
        if (!name) return "";
        const parts = name.split(" ");
        if (parts.length === 1) return name;
        const firstInitial = parts[0][0];
        const lastName = parts.slice(1).join(" ");
        return `${firstInitial} ${lastName}`;
    };

    const handleSpreadTabClick = () => {
        if (window.innerWidth >= 992) return;

        if (mobileColumnTab === 'spread') {
            onShowMoneylineSlotChange(prev => !prev);
            return;
        }

        onMobileColumnTabChange('spread');
        onShowMoneylineSlotChange(false);
    };

    const handleTotalTabClick = () => {
        if (window.innerWidth >= 992) return;

        onMobileColumnTabChange(prev => prev === 'teamTotal' ? 'spread' : 'teamTotal');
        onShowMoneylineSlotChange(false);
    };

    return (
        <>
            {showHeaderByLeague && game && (
                <TableComponents
                    game={game}
                    mobileColumnTab={mobileColumnTab}
                    onSpreadTabClick={handleSpreadTabClick}
                    onTotalTabClick={handleTotalTabClick}
                />
            )}
            <div className='gamesGroup'>
                {showHeaderByDate && (
                    <div className='d-flex'>
                        <div className="header-table">
                            <div className="container-date team-name-container">
                                <span className="group ps-1">{formattedDate}</span>
                                <span className="group"></span>
                            </div>
                            <div className="spread-container center-items">
                                <span className='group'>
                                </span>
                            </div>
                            <div className="moneyline-container center-items d-none d-lg-block">
                                <span className='group'></span>
                            </div>
                            <div className="total-container center-items">
                                <span className='group'></span>
                            </div>
                            <div className="over-under-container center-items d-none d-lg-block">
                                <span className='group'></span>
                            </div>
                        </div>
                    </div>
                )}
                <div className={`container-time${isGolf ? ' container-time--golf' : ''}`}>
                    {isGolf ? (
                        <div className="container-time-info">
                            <span className="group-text teko">{time}</span>
                            {(game.tournament?.name || game.tournament_stage) && (
                                <span className="tournament-event-info">
                                    {' '}- {formatGolfEventLabel(game)}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="group-text teko">{time}</span>
                    )}
                    {!isGolf && (
                        <div className="live">
                            <Link className={`props-game teko no-link ${props.rowIndex !== undefined && props.rowIndex % 2 === 0 ? 'green' : 'red'
                                }`}
                                to={
                                    `props?` +
                                    `id=${encodeURIComponent(game.id)}` +
                                    `&league=${encodeURIComponent((game.league.id))}` +
                                    `&sport=${encodeURIComponent(game.sport.id)}` +
                                    `&home=${encodeURIComponent(game.home_competitors[0]?.name)}` +
                                    `&away=${encodeURIComponent(game.away_competitors[0]?.name)}` +
                                    `&homeLogo=${encodeURIComponent(game.home_competitors[0]?.logo)}` +
                                    `&awayLogo=${encodeURIComponent(game.away_competitors[0]?.logo)}` +
                                    `&gameId=${encodeURIComponent(game.game_id)}` +
                                    `&GameDate=${encodeURIComponent(game.start_date)}` +
                                    `&logoLeague=${encodeURIComponent(game.leagueLogo)}`
                                }
                                onClick={(e) => {
                                    if (user.status !== "Active") {
                                        e.preventDefault();
                                        setErrorMessage("Your account is in read-only mode."
                                        );
                                        return;
                                    }
                                }}>
                                MORE BETS +
                            </Link>
                        </div>
                    )}
                </div>

                <div>
                    <GameTeamComponent
                        gameId={game.game_id!}
                        fixture_id={game.id}
                        sport={game.sport?.name}
                        gameTime={gameTimeDate}
                        team={game.away_team_display!}
                        league={game.league}
                        odds={organizedOdds[1] ?? []}
                        onShowBetsModal={onShowBetsModal}
                        gameLogo={game.away_competitors[0]?.logo}
                        opposingTeam={game.home_team_display}
                        pitcherHome={game.home_starter}
                        pitcherAway={game.away_starter}
                        pitcherHomeId={game.home_starter_id}
                        pitcherAwayId={game.away_starter_id}
                        pitcherCheckedAway={pitchersChecked?.away ?? false}
                        pitcherCheckedHome={pitchersChecked?.home ?? false}
                        setLoadingBet={setLoadingBet}
                        loadingBet={loadingBet}
                        showFlag={true}
                        logoLeague={game.leagueLogo}
                        setErrorMessage={setErrorMessage}
                        user={user}
                        mobileColumnTab={mobileColumnTab}
                        showMoneylineSlot={showMoneylineSlot}
                    />

                    <GameTeamComponent
                        gameId={game.game_id!}
                        fixture_id={game.id}
                        sport={game.sport?.name}
                        gameTime={gameTimeDate}
                        team={game.home_team_display!}
                        league={game.league}
                        odds={organizedOdds[0] ?? []}
                        onShowBetsModal={onShowBetsModal}
                        gameLogo={game.home_competitors[0]?.logo}
                        opposingTeam={game.away_team_display}
                        pitcherHome={game.home_starter}
                        pitcherAway={game.away_starter}
                        pitcherHomeId={game.home_starter_id}
                        pitcherAwayId={game.away_starter_id}
                        pitcherCheckedAway={pitchersChecked?.away ?? false}
                        pitcherCheckedHome={pitchersChecked?.home ?? false}
                        setLoadingBet={setLoadingBet}
                        loadingBet={loadingBet}
                        showFlag={false}
                        logoLeague={game.leagueLogo}
                        setErrorMessage={setErrorMessage}
                        user={user}
                        mobileColumnTab={mobileColumnTab}
                        showMoneylineSlot={showMoneylineSlot}
                    />

                    {theRestOfOdds.length > 0 &&
                        theRestOfOdds.map((oddsGroup, index) => (
                            <GameTeamComponent
                                key={index}
                                gameId={game.game_id!}
                                fixture_id={game.id}
                                sport={game.sport?.name}
                                gameTime={gameTimeDate}
                                team="Draw"
                                league={game.league}
                                odds={oddsGroup}
                                onShowBetsModal={onShowBetsModal}
                                gameLogo={""}
                                opposingTeam={game.home_team_display}
                                pitcherHome={game.home_starter}
                                pitcherAway={game.away_starter}
                                pitcherHomeId={game.home_starter_id}
                                pitcherAwayId={game.away_starter_id}
                                pitcherCheckedAway={pitchersChecked?.away ?? false}
                                pitcherCheckedHome={pitchersChecked?.home ?? false}
                                setLoadingBet={setLoadingBet}
                                loadingBet={loadingBet}
                                showFlag={true}
                                logoLeague={game.leagueLogo}
                                setErrorMessage={setErrorMessage}
                                user={user}
                                mobileColumnTab={mobileColumnTab}
                                showMoneylineSlot={showMoneylineSlot}
                            />
                        ))}
                </div>

                {game.league.name === "MLB" &&
                    game.odds?.some(odd => !odd.market.startsWith("1st")) && (
                        <div className="pitchers-row">
                            <label className="pitcher-checkbox">
                                <span className="pitcher-name">{formatPitcherName(game.away_starter) || "TBD"} - R</span>
                                <input
                                    type="checkbox"
                                    className="checkbox-pitcher"
                                    checked={!!pitchersChecked?.away}
                                    onChange={() => handlePitcherChangeInternal("away")}
                                />
                            </label>

                            <label className="pitcher-checkbox right">
                                <input
                                    type="checkbox"
                                    className="checkbox-pitcher"
                                    checked={!!pitchersChecked?.home}
                                    onChange={() => handlePitcherChangeInternal("home")}
                                />
                                <span className="pitcher-name">{formatPitcherName(game.home_starter) || "TBD"} - R</span>
                            </label>
                        </div>
                    )}

            </div>
        </>
    )
}